import { useEffect, useRef, useState, type FormEvent } from 'react';
import { ApiError, checkResetCode, forgotPassword, resetPassword, type AuthOptions } from '../api';
import { MIN_PASSWORD_LENGTH, cleanCodeInput, looksLikeEmail, newPasswordProblem, parseResetFragment } from '../password';
import { Logo } from './icons';
import { BotCheckError, Turnstile, type BotCheckHandle } from './Turnstile';
import { PasswordField } from './PasswordField';
import { StrengthMeter } from './StrengthMeter';

type Step = 'email' | 'code' | 'checking' | 'password';

const errorText = (err: unknown, fallback: string) =>
  err instanceof ApiError || err instanceof BotCheckError ? err.message : fallback;

/**
 * "Forgot password?": email → 6-digit code → new password. The email's
 * "Reset password" button opens /reset#email=…&code=…, which skips straight to
 * the new password (the code is checked first).
 */
export function ResetPassword({
  options,
  initialEmail,
  onDone,
  onBack,
}: {
  options: AuthOptions | null;
  initialEmail: string;
  onDone: (email: string) => void;
  onBack: (email: string) => void;
}) {
  // Read the email button's #email=…&code=… once, during the first render, and take it
  // out of the address bar at once: before any child (e.g. the Cloudflare script) loads.
  const [link] = useState(() => {
    const l = parseResetFragment(window.location.hash);
    if (window.location.hash) history.replaceState(history.state, '', window.location.pathname);
    return l;
  });
  const [step, setStep] = useState<Step>(link ? 'checking' : 'email');
  const [email, setEmail] = useState(link?.email ?? initialEmail);
  const [code, setCode] = useState(link?.code ?? '');
  const [password, setPassword] = useState('');
  const [repeat, setRepeat] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [minutes, setMinutes] = useState(10);
  const [fromLink, setFromLink] = useState(link !== null);
  const siteKey = options?.turnstileSiteKey ?? null;
  // one bot check per screen; it runs when the form is sent (PR B2: also on the code steps)
  const bot = useRef<BotCheckHandle>(null);
  const started = useRef(false);

  // Opened from the email's button: check the code from the #fragment (taken out of
  // the address bar before anything renders, see `link` above), once the "Checking
  // your code…" card and its bot check are on screen and the options are known.
  const [optionsLate, setOptionsLate] = useState(false);
  useEffect(() => {
    if (options !== null) return;
    const t = setTimeout(() => setOptionsLate(true), 8000); // options never came: try anyway
    return () => clearTimeout(t);
  }, [options]);
  useEffect(() => {
    if (!link || started.current || step !== 'checking' || (options === null && !optionsLate)) return;
    started.current = true;
    (async () => {
      const token = await bot.current?.run();
      return checkResetCode(link.email, link.code, token);
    })().then(
      () => setStep('password'),
      (err) => {
        setStep('code');
        setError(errorText(err, 'Couldn’t check the code. Try again.'));
      },
    );
  }, [link, step, options, optionsLate]);

  async function sendCode(e: FormEvent) {
    e.preventDefault();
    if (busy) return;
    if (!looksLikeEmail(email)) {
      setError('Enter the full email address you log in with, like name@example.com.');
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const r = await forgotPassword(email.trim(), await bot.current?.run());
      setMinutes(r.minutes);
      setCode('');
      setStep('code');
    } catch (err) {
      setError(errorText(err, 'Couldn’t send the code. Try again.'));
    } finally {
      setBusy(false);
    }
  }

  async function checkCode(e: FormEvent) {
    e.preventDefault();
    if (busy || code.length !== 6) return;
    setBusy(true);
    setError(null);
    try {
      await checkResetCode(email.trim(), code, await bot.current?.run());
      setStep('password');
    } catch (err) {
      setError(errorText(err, 'Couldn’t check the code. Try again.'));
    } finally {
      setBusy(false);
    }
  }

  async function setNew(e: FormEvent) {
    e.preventDefault();
    const problem = newPasswordProblem(password, repeat);
    if (problem) {
      setError(problem);
      return;
    }
    if (busy) return;
    setBusy(true);
    setError(null);
    try {
      await resetPassword(email.trim(), code, password, await bot.current?.run());
      onDone(email.trim());
    } catch (err) {
      setError(errorText(err, 'Couldn’t change the password. Try again.'));
      setBusy(false);
      if (err instanceof ApiError && err.code === 'wrong_code') {
        setStep('code'); // the code ran out meanwhile: ask for a new one
      }
    }
  }

  const back = (
    <button type="button" className="link-btn login-back" onClick={() => onBack(email.trim())}>
      Back to log in
    </button>
  );
  const errorLine = error && (
    <p className="login-error" role="alert">
      {error}
    </p>
  );

  if (step === 'checking') {
    return (
      <main className="login">
        <div className="login-card" aria-busy="true">
          <Logo />
          <h1>Reset your password</h1>
          <p className="login-notice">Checking your code…</p>
          <Turnstile ref={bot} siteKey={siteKey} action="reset" onSolved={() => setError(null)} />
        </div>
      </main>
    );
  }

  if (step === 'email') {
    return (
      <main className="login">
        <form className="login-card" onSubmit={sendCode} noValidate>
          <Logo />
          <h1>Reset your password</h1>
          <p className="login-notice">Enter the email you log in with. We’ll send you a 6-digit code.</p>
          <label className="field">
            <span>Email</span>
            <input
              type="email"
              name="email"
              autoComplete="username"
              inputMode="email"
              required
              autoFocus
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
          </label>
          <Turnstile ref={bot} siteKey={siteKey} action="forgot" onSolved={() => setError(null)} />
          {errorLine}
          <button
            className="btn btn-primary btn-block"
            type="submit"
            disabled={busy || !looksLikeEmail(email)}
          >
            {busy ? 'Sending…' : 'Send code'}
          </button>
          {back}
        </form>
      </main>
    );
  }

  if (step === 'code') {
    return (
      <main className="login">
        <form className="login-card" onSubmit={checkCode} noValidate>
          <Logo />
          <h1>Enter your code</h1>
          {!fromLink && (
            <p className="login-notice">
              If an account exists for <strong className="login-email">{email.trim()}</strong>, we’ve sent it a 6-digit
              code. It expires in {minutes} minutes. Check your spam folder if you don’t see it.
            </p>
          )}
          <label className="field">
            <span>Code</span>
            <input
              className="code-input"
              type="text"
              name="code"
              inputMode="numeric"
              autoComplete="one-time-code"
              pattern="[0-9]*"
              maxLength={7}
              required
              autoFocus
              value={code}
              onChange={(e) => setCode(cleanCodeInput(e.target.value))}
            />
          </label>
          <Turnstile ref={bot} siteKey={siteKey} action="reset" onSolved={() => setError(null)} />
          {errorLine}
          <button className="btn btn-primary btn-block" type="submit" disabled={busy || code.length !== 6}>
            {busy ? 'Checking…' : 'Continue'}
          </button>
          <p className="login-help">
            Didn’t get it?{' '}
            <button
              type="button"
              className="link-btn"
              onClick={() => {
                setError(null);
                setFromLink(false);
                setStep('email');
              }}
            >
              Send a new code
            </button>
          </p>
          {back}
        </form>
      </main>
    );
  }

  return (
    <main className="login">
      <form className="login-card" onSubmit={setNew} noValidate>
        <Logo />
        <h1>Choose a new password</h1>
        <p className="login-notice">
          For <strong className="login-email">{email.trim()}</strong>. At least {MIN_PASSWORD_LENGTH} characters. You’ll be
          signed out on all your devices.
        </p>
        {/* lets password managers save the new password for the right account */}
        <input type="email" name="email" autoComplete="username" value={email.trim()} readOnly hidden />
        <PasswordField
          label="New password"
          name="new-password"
          autoComplete="new-password"
          required
          autoFocus
          value={password}
          onChange={setPassword}
        >
          <StrengthMeter password={password} email={email} />
        </PasswordField>
        <PasswordField
          label="Repeat new password"
          name="repeat-password"
          autoComplete="new-password"
          required
          value={repeat}
          onChange={setRepeat}
        />
        <Turnstile ref={bot} siteKey={siteKey} action="reset" onSolved={() => setError(null)} />
        {errorLine}
        <button className="btn btn-primary btn-block" type="submit" disabled={busy || !password || !repeat}>
          {busy ? 'Saving…' : 'Set new password'}
        </button>
        {back}
      </form>
    </main>
  );
}
